/*
 * Modern Standard Coffee — keeps the free UK delivery message and the Passport
 * note in step with the cart without a page reload.
 *
 * Markup comes from snippets/msc-free-delivery.liquid and
 * snippets/msc-passport-note.liquid, which render the correct first state on
 * the server. This file only updates them after the cart changes on the page:
 * quick add, the add-to-cart pop-up, cart page quantity changes and removals,
 * the cart drawer, and anything else that calls /cart/add, /cart/change,
 * /cart/update or /cart/clear (including app widgets).
 *
 * Top bar (draft 29, 17 Oct 2026): context 'bar' in the announcement bar on
 * every page; basket only, never hidden, see snippets/msc-free-delivery.liquid.
 * On product pages the bar carries data-variant-prices too, so it counts the
 * coffee on screen like the old product-page message and uses its wording.
 * Straight after a successful add to basket the bar shows the basket only
 * (so the bag just added isn't counted twice) until the shopper changes
 * size, bag, grind, quantity or purchase option again.
 *
 * Product pages (draft 24, 15 Oct 2026): the message counts the coffee on
 * screen as well as the basket, so a £48 Mix & Match says "Free UK delivery on
 * this order" instead of "You're £30.00 away". It follows size / bag changes.
 */
(function () {
  if (window.__mscFreeDelivery) return;
  window.__mscFreeDelivery = true;

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  var lastCart = null;
  var afterAdd = false; // basket-only on the top bar until the shopper changes a choice
  var CART_ADD = /\/cart\/add(\.js)?$/;

  function isCartAdd(url) {
    try { return CART_ADD.test(new URL(url, window.location.origin).pathname); } catch (e) { return false; }
  }

  function formatPence(pence) {
    return money.format(pence / 100);
  }

  function isCartWrite(url) {
    try {
      var path = new URL(url, window.location.origin).pathname;
      return CART_WRITE.test(path);
    } catch (e) {
      return false;
    }
  }

  // ---- Work out the basket value that counts towards free delivery ----------

  function eligibleValue(cart, excludedTypes) {
    var excluded = 0;
    var hasEligible = false;
    (cart.items || []).forEach(function (item) {
      var noShipping = item.requires_shipping === false;
      var excludedType = excludedTypes.indexOf(item.product_type) !== -1;
      if (noShipping || excludedType) {
        excluded += item.final_line_price || 0;
      } else {
        hasEligible = true;
      }
    });
    var value = (cart.total_price || 0) - excluded;
    return { value: value < 0 ? 0 : value, hasEligible: hasEligible };
  }

  // ---- Product page: the coffee currently selected on screen -----------------

  function selectedItemValue(el) {
    var raw = el.getAttribute('data-variant-prices');
    if (!raw) return 0;
    var prices;
    try { prices = JSON.parse(raw); } catch (e) { return 0; }
    var form = el.closest('form');
    if (!form) {
      var sec = el.closest('[id^="shopify-section"]');
      form = sec && sec.querySelector('form[action*="/cart/add"]');
    }
    // The top bar sits outside the product form: use the add-to-cart form
    // whose variant id belongs to this product.
    var own = function (f) {
      var i = f && f.querySelector('[name="id"]');
      return !!(i && prices[String(i.value)] !== undefined);
    };
    if (!own(form)) {
      form = null;
      document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
        if (!form && own(f)) form = f;
      });
    }
    var idInput = form && form.querySelector('[name="id"]');
    var id = idInput ? String(idInput.value) : el.getAttribute('data-current-variant');
    var price = prices[id] || 0;
    var qtyInput = form && form.querySelector('[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!qty || qty < 1) qty = 1;
    return price * qty;
  }

  function updateFreeDelivery(cart) {
    document.querySelectorAll('[data-msc-fd]').forEach(function (el) {
      var threshold = parseInt(el.getAttribute('data-threshold'), 10);
      if (!threshold) return;
      var excludedTypes = (el.getAttribute('data-excluded-types') || '')
        .split('|').map(function (s) { return s.trim(); }).filter(Boolean);
      var result = eligibleValue(cart, excludedTypes);
      var context = el.getAttribute('data-context');

      // Outside the product page there's nothing to say about an empty basket
      // or one that only holds free-shipping items. The top bar (draft 29) is
      // never hidden: it falls back to the plain "orders over £30" line.
      var isBar = context === 'bar';
      var withItem = el.hasAttribute('data-variant-prices') && !(isBar && afterAdd);
      el.hidden = context !== 'pdp' && !isBar && !result.hasEligible;
      el.classList.toggle('is-empty', isBar && !withItem && !result.hasEligible);

      var value = result.value;
      if (withItem) value += selectedItemValue(el);

      var remaining = threshold - value;
      var text = el.querySelector('[data-msc-fd-text]');
      var fill = el.querySelector('[data-msc-fd-fill]');
      var unlocked = remaining <= 0;

      var html;
      if (isBar && withItem) {
        html = unlocked
          ? '<strong>Free UK delivery</strong> on this order'
          : 'Add <strong>' + formatPence(remaining) + '</strong> more for free UK delivery';
      } else if (isBar) {
        if (!result.hasEligible) {
          html = 'Free UK delivery on orders over ' + formatPence(threshold).replace(/\.00$/, '');
        } else {
          html = unlocked
            ? '<strong>You’ve got free UK delivery</strong>'
            : 'You’re <strong>' + formatPence(remaining) + '</strong> away from free UK delivery';
        }
      } else if (context === 'pdp') {
        html = unlocked
          ? '<strong>Free UK delivery</strong> on this order.'
          : 'Add <strong>' + formatPence(remaining) + '</strong> more for free UK delivery.';
      } else {
        html = unlocked
          ? '<strong>You’ve unlocked free UK delivery.</strong>'
          : 'You’re <strong>' + formatPence(remaining) + '</strong> away from free UK delivery.';
      }
      if (text && text.innerHTML !== html) text.innerHTML = html;

      el.classList.toggle('is-unlocked', unlocked);
      if (fill) {
        var pct = unlocked ? 100 : Math.floor((value / threshold) * 100);
        fill.style.width = pct + '%';
      }
    });
  }

  function updatePassportNote(cart) {
    var hasItem = (cart.items || []).some(function (item) { return !item.gift_card; });
    document.querySelectorAll('[data-msc-passport-note]').forEach(function (el) {
      el.hidden = !hasItem;
    });
  }

  function apply(cart) {
    if (!cart || !cart.items) return;
    lastCart = cart;
    updateFreeDelivery(cart);
    updatePassportNote(cart);
  }

  // ---- Fetch the cart after any change -------------------------------------

  var timer = null;
  var requestId = 0;

  function refresh() {
    if (!nativeFetch) return;
    var id = ++requestId;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    nativeFetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { if (id === requestId) apply(cart); })
      .catch(function () {});
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  }

  window.mscRefreshCartMessages = schedule;

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = nativeFetch(input, init);
      if (isCartWrite(url)) {
        promise.then(function (res) {
          if (!res || !res.ok) return;
          if (isCartAdd(url)) afterAdd = true;
          schedule();
        }).catch(function () {});
      }
      return promise;
    };
  }

  // Some app widgets still use XMLHttpRequest.
  if (window.XMLHttpRequest) {
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartWrite(String(url))) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            if (isCartAdd(String(url))) afterAdd = true;
            schedule();
          }
        });
      }
      return open.apply(this, arguments);
    };
  }

  // The cart page already announces its own updates.
  document.addEventListener('vs:updatedCart', schedule);

  // Back/forward cache can restore a page with an out-of-date basket.
  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });

  // ---- Product page: follow size / bag / quantity changes ------------------
  // The variant id lives in a hidden input that scripts update without firing
  // an event, so check it on any form change and on a light timer.
  if (document.querySelector('[data-msc-fd][data-variant-prices]')) {
    var lastKey = '';
    var check = function () {
      var key = '';
      document.querySelectorAll('[data-msc-fd][data-variant-prices]').forEach(function (el) {
        key += selectedItemValue(el) + '|';
      });
      if (key !== lastKey) {
        if (lastKey !== '') afterAdd = false; // size, bag or quantity changed
        lastKey = key;
        if (lastCart) updateFreeDelivery(lastCart); else refresh();
      }
    };
    document.addEventListener('change', function (e) {
      // Grind or purchase option changed in a product form: count the
      // on-screen selection again.
      if (afterAdd && e.target && e.target.closest && e.target.closest('form[action*="/cart/add"]')) {
        afterAdd = false;
        if (lastCart) updateFreeDelivery(lastCart);
      }
      setTimeout(check, 50);
    });
    document.addEventListener('click', function () { setTimeout(check, 80); });
    setInterval(check, 600);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', refresh);
    } else {
      refresh();
    }
  }
})();

/*
 * Draft 31 (18 Oct 2026): on phones, fade the Passport (Influence.io) launcher
 * while the product buy box is on screen so it never covers the size or
 * purchase rows. Sets html.msc-buybox-onscreen; the styling lives in
 * assets/msc-so-calm.css. Only runs on product pages with the msc-so form.
 */
(function () {
  if (window.__mscBuyboxWatch) return;
  window.__mscBuyboxWatch = true;
  function init() {
    var form = document.querySelector('.msc-so form.msc-so__form');
    if (!form || !('IntersectionObserver' in window)) return;
    var root = document.documentElement;
    var mq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    var visible = false;
    var sync = function () {
      root.classList.toggle('msc-buybox-onscreen', visible && (!mq || mq.matches));
    };
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { visible = e.isIntersecting; });
      sync();
    }, { threshold: 0 }).observe(form);
    if (mq && mq.addEventListener) mq.addEventListener('change', sync);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
